import {
  ASSET_CLASS_DISPLAY_NAMES,
  ASSET_CLASS_MANUAL_ADD,
  ASSET_SUBCLASS_DISPLAY_NAMES,
  DEPOSITORY_SUB_TYPES_MANUAL_ADD,
  DEPRECATED_ACCOUNT_SUBTYPE_DISPLAY_NAMES,
  INVESTMENT_SUB_TYPES_MANUAL_ADD,
  LIABILITY_TYPE_DISPLAY_NAME,
  LIABILITY_TYPES_MANUAL_ADD,
  REAL_ESTATE_SUBCLASSES_MANUAL_ADD,
} from 'src/constants/account-link';

import type {
  AccountSubtypeName,
  AssetClassName,
  AssetSubclassName,
  LiabilityTypeName,
} from '@savvy/constants/generated/gql-enums';
import {
  ASSET_CLASS_COLLECTIBLE,
  ASSET_CLASS_COMPANY_EQUITY,
  ASSET_CLASS_INSURANCE_POLICY,
  ASSET_CLASS_OTHER,
  ASSET_CLASS_REAL_ESTATE,
  ASSET_CLASS_VEHICLE,
} from '@savvy/constants/asset-classes';
import {
  LIABILITY_TYPE_AUTO,
  LIABILITY_TYPE_COMMERCIAL,
  LIABILITY_TYPE_CONSUMER,
  LIABILITY_TYPE_GENERAL,
  LIABILITY_TYPE_HOME_EQUITY,
  LIABILITY_TYPE_LINE_OF_CREDIT,
  LIABILITY_TYPE_MORTGAGE,
  LIABILITY_TYPE_OTHER,
  LIABILITY_TYPE_STUDENT,
} from '@savvy/constants/liability-types';

export const MAX_NAME_LENGTH = 255;
export const MAX_NOTES_LENGTH = 5000;

export const ADD_ACCOUNT_CANCEL_LABEL = 'Cancel';
export const ADD_ACCOUNT_BACK_LABEL = 'Back';
export const ADD_ACCOUNT_SAVE_ERROR = "We couldn't save that. Try again in a moment.";

export const CONNECT_FEED_TITLE = 'Connect your accounts';
export const CONNECT_FEED_DESCRIPTION = 'Keep your balances and details up to date automatically.';
export const CONNECT_MANUAL_LABEL = 'Enter details manually';
export const CONNECT_MANUAL_DESCRIPTION = 'Track a balance you update yourself.';

export const ACCOUNT_FORM_TITLES = {
  bank: 'Add a bank account',
  investment: 'Add an investment account',
  card: 'Connect a credit card',
} as const;

export const CONNECT_CARD_FEED_ONLY_NOTE =
  'Connect your card so its balance updates itself. To track a card by hand, add it as a liability instead.';

export function mfaFallbackNote(institution: string | null | undefined) {
  const named =
    institution === null || institution === undefined || institution === '' ? 'that institution' : institution;
  return `We can't link ${named} automatically because it uses multi-factor authentication. Enter the balance manually and you can update it whenever you like.`;
}

export const ASSET_FORM_TITLES = {
  realEstate: 'Real estate details',
  other: 'Add an asset',
} as const;

export const LIABILITY_FORM_TITLE = 'Add a liability';

export const EQUITY_BASICS_TITLE = 'Equity details';
export const EQUITY_GRANT_TITLE = 'Grant details';
export const EQUITY_GRANT_QUESTION_TITLE = 'Do you want to add details of the grant?';
export const EQUITY_VALUE_TITLE = 'Equity value';
export const EQUITY_PUBLIC_ONLY_NOTE =
  'We support public company equity at the moment. If you\u2019d like to add private company equity, use the \u2018Other asset\u2019 type.';
export const EQUITY_TICKER_DESCRIPTION =
  'The short name for the company stock. Usually one to five letters. For example, GOOGL or AAPL.';
export const EQUITY_ASSET_DESCRIPTION = 'Company equity';

export const EQUITY_GRANT_TYPE_OPTIONS = [
  { value: 'ISO', label: 'ISO' },
  { value: 'NSO', label: 'NSO' },
  { value: 'RSU', label: 'RSU' },
] as const;

export const EQUITY_VESTING_RATE_OPTIONS = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Yearly', label: 'Yearly' },
] as const;

export const EQUITY_GRANT_TYPES_WITH_EXERCISE_PRICE: readonly string[] = ['ISO', 'NSO'];
export const MORTGAGE_STEP_TITLE = 'Do you have a mortgage on this property?';
export const MORTGAGE_FORM_TITLE = 'Mortgage details';
export const MORTGAGE_ADD_LABEL = 'Add mortgage';
export const MORTGAGE_SKIP_LABEL = 'Not now';

export const NOTES_PLACEHOLDER = 'Account numbers, important dates, or other context';
export const NOTES_DESCRIPTION = 'Optional';

type Option<Value extends string> = {
  readonly value: Value;
  readonly label: string;
};

function options<Value extends string>(values: readonly Value[], labels: Record<Value, string>) {
  return values.map((value) => ({ value, label: labels[value] }));
}

export const DEPOSITORY_SUBTYPE_OPTIONS: readonly Option<AccountSubtypeName>[] = options(
  DEPOSITORY_SUB_TYPES_MANUAL_ADD,
  DEPRECATED_ACCOUNT_SUBTYPE_DISPLAY_NAMES,
);

export const INVESTMENT_SUBTYPE_OPTIONS: readonly Option<AccountSubtypeName>[] = options(
  INVESTMENT_SUB_TYPES_MANUAL_ADD,
  DEPRECATED_ACCOUNT_SUBTYPE_DISPLAY_NAMES,
);

export const REAL_ESTATE_SUBCLASS_OPTIONS: readonly Option<AssetSubclassName>[] = options(
  REAL_ESTATE_SUBCLASSES_MANUAL_ADD,
  ASSET_SUBCLASS_DISPLAY_NAMES,
);

export const MANUAL_ASSET_CLASS_OPTIONS: readonly Option<AssetClassName>[] = options(
  ASSET_CLASS_MANUAL_ADD,
  ASSET_CLASS_DISPLAY_NAMES,
);

export const LIABILITY_TYPE_OPTIONS: readonly Option<LiabilityTypeName>[] = options(
  LIABILITY_TYPES_MANUAL_ADD,
  LIABILITY_TYPE_DISPLAY_NAME,
);

const EVERY_ASSET_CLASS: readonly AssetClassName[] = [
  ASSET_CLASS_COLLECTIBLE,
  ASSET_CLASS_COMPANY_EQUITY,
  ASSET_CLASS_INSURANCE_POLICY,
  ASSET_CLASS_OTHER,
  ASSET_CLASS_REAL_ESTATE,
  ASSET_CLASS_VEHICLE,
];

const EVERY_LIABILITY_TYPE: readonly LiabilityTypeName[] = [
  LIABILITY_TYPE_AUTO,
  LIABILITY_TYPE_COMMERCIAL,
  LIABILITY_TYPE_CONSUMER,
  LIABILITY_TYPE_GENERAL,
  LIABILITY_TYPE_HOME_EQUITY,
  LIABILITY_TYPE_LINE_OF_CREDIT,
  LIABILITY_TYPE_MORTGAGE,
  LIABILITY_TYPE_OTHER,
  LIABILITY_TYPE_STUDENT,
];

export function resolveAssetClass(value: string): AssetClassName | undefined {
  return EVERY_ASSET_CLASS.find((assetClass) => assetClass === value);
}

export function resolveLiabilityType(value: string): LiabilityTypeName | undefined {
  return EVERY_LIABILITY_TYPE.find((type) => type === value);
}
